import { useCallback, useMemo } from 'react';
import analyticsService from '../services/analytics/analyticsService';
import userTrackingService from '../services/users/userTrackingService';
import { createFileLogger } from '../utils/logger';

interface CTAClickData {
  button_text?: string;
  button_id?: string;
  page_path?: string;
  section?: string;
  [key: string]: any; // Allow additional properties
}

interface KeyActionData {
  action_type: string;
  success?: boolean;
  duration_ms?: number;
  metadata?: Record<string, any>;
}

export const useAnalytics = () => {
  // CRITICAL FIX: Memoize log creation so it doesn't change every render
  // This ensures all callbacks derived from it also remain stable
  const log = useMemo(() => createFileLogger('ANALYTICS_HOOK'), []);

  // Track CTA button clicks - DISABLED
  const trackCTAClick = useCallback((buttonName: string, additionalData?: CTAClickData) => {
    log.debug('[CTA_CLICK] CTA click tracking disabled:', { buttonName, additionalData });
    // CTA tracking disabled - no Mixpanel event sent
  }, [log]);

  // Track key user actions (magic moments) - DISABLED
  const trackKeyAction = useCallback((actionName: string, data: KeyActionData) => {
    log.debug('[KEY_ACTION] Key action tracking disabled:', { actionName, data });
    // Key action tracking disabled - no Mixpanel event sent
  }, [log]);

  // Track app opens
  const trackAppOpen = useCallback((appName: string, context?: Record<string, any>) => {
    const eventData = {
      name: 'App Opened',
      category: 'app_usage',
      properties: {
        app_name: appName,
        timestamp: new Date().toISOString(),
        user_id: userTrackingService.getCurrentUserId(),
        session_id: userTrackingService.getSessionId(),
        ...context
      }
    };

    log.debug('[APP_OPEN] Tracking app open:', eventData);
    analyticsService.track(eventData);
  }, [log]);

  // Track successful login
  const trackLogin = useCallback((method: string, userId: string, userInfo?: Record<string, any>) => {
    // Update user tracking service
    userTrackingService.setUser(userId);
    
    if (userInfo) {
      userTrackingService.updateUserInfo(userInfo);
    }

    // Track the login event
    analyticsService.trackLogin(method, userId);

    // Set comprehensive user properties
    const userProperties = {
      user_id: userId,
      properties: {
        // ✅ Filter out empty strings - only set if truthy and not empty
        ...(userInfo?.email && userInfo.email.trim() !== '' && { $email: userInfo.email }),
        ...(userInfo?.name && userInfo.name.trim() !== '' && { $name: userInfo.name }),
        login_method: method,
        last_login: new Date().toISOString(),
        total_logins: (userInfo?.total_logins || 0) + 1,
        user_type: 'authenticated',
        ...userInfo
      }
    };

    analyticsService.setUserProperties(userProperties);
    
    log.debug('[LOGIN] User logged in and tracked:', { userId, method });
  }, [log]);

  // Track successful signup
  const trackSignup = useCallback(async (method: string, userId: string, userInfo?: Record<string, any>) => {
    // Update user tracking service
    userTrackingService.setUser(userId);
    
    if (userInfo) {
      userTrackingService.updateUserInfo(userInfo);
    }

    // Track the signup event with all provided properties
    analyticsService.trackSignup(method, userId, userInfo);

    // Set comprehensive user properties for new user
    const userProperties = {
      user_id: userId,
      properties: {
        // ✅ Filter out empty strings - only set if truthy and not empty
        ...(userInfo?.email && userInfo.email.trim() !== '' && { $email: userInfo.email }),
        ...(userInfo?.name && userInfo.name.trim() !== '' && { $name: userInfo.name }),
        signup_method: method,
        signup_date: new Date().toISOString(),
        total_logins: 1,
        user_type: 'authenticated',
        is_new_user: true,
        ...userInfo
      }
    };

    analyticsService.setUserProperties(userProperties);
    
    log.debug('[SIGNUP] User signed up and tracked:', { userId, method });
    
    // Flush events before any navigation to prevent event loss
    await analyticsService.flushEvents();
  }, [log]);

  // Track document clicks
  const trackDocClick = useCallback((docId: string, docName: string, context?: Record<string, any>) => {
    const eventData = {
      name: 'Document Clicked',
      category: 'document_interaction',
      properties: {
        doc_id: docId,
        doc_name: docName,
        user_id: userTrackingService.getCurrentUserId(),
        session_id: userTrackingService.getSessionId(),
        timestamp: new Date().toISOString(),
        ...context
      }
    };

    log.debug('[DOC_CLICK] Tracking document click:', eventData);
    analyticsService.track(eventData);
  }, [log]);

  // Track document shares
  const trackDocShare = useCallback((docId: string, shareMethod: string, context?: Record<string, any>) => {
    const eventData = {
      name: 'Document Shared',
      category: 'document_sharing',
      properties: {
        doc_id: docId,
        share_method: shareMethod,
        user_id: userTrackingService.getCurrentUserId(),
        session_id: userTrackingService.getSessionId(),
        timestamp: new Date().toISOString(),
        ...context
      }
    };

    log.debug('[DOC_SHARE] Tracking document share:', eventData);
    analyticsService.track(eventData);
  }, [log]);

  // Track error pages specifically (not just general errors)
  const trackErrorPage = useCallback((errorType: string, errorMessage?: string, context?: Record<string, any>) => {
    const eventData = {
      name: 'Error Page Viewed',
      category: 'error_tracking',
      properties: {
        error_type: errorType,
        error_message: errorMessage,
        page_path: window.location.pathname,
        user_id: userTrackingService.getCurrentUserId(),
        session_id: userTrackingService.getSessionId(),
        timestamp: new Date().toISOString(),
        ...context
      }
    };

    log.debug('[ERROR_PAGE] Tracking error page view:', eventData);
    analyticsService.track(eventData);
  }, [log]);

  return {
    trackCTAClick,
    trackKeyAction,
    trackAppOpen,
    trackLogin,
    trackSignup,
    trackDocClick,
    trackDocShare,
    trackErrorPage,
    // Re-export some analytics service methods for convenience
    track: analyticsService.track.bind(analyticsService),
    trackPageView: analyticsService.trackPageView.bind(analyticsService),
    setUserProperties: analyticsService.setUserProperties.bind(analyticsService)
  };
};

export default useAnalytics; 